import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

/**
 * PrintReportHeader
 * Renders an official header for printed statements across all device sizes.
 * Hidden on screen via `.print-only` and displayed prominently during `@media print`.
 */
export default function PrintReportHeader({
  title,
  subtitle,
  period,
  account,
  officer,
  metrics = [],
}) {
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const currentTime = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      className="print-only"
      sx={{
        display: 'none',
        width: '100%',
        mb: 2.5,
        pb: 1.5,
        borderBottom: '2.5px solid #0B3B60',
      }}
    >
      {/* Top Organization Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '15pt',
              fontWeight: 900,
              color: '#0B3B60',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              lineHeight: 1.15,
            }}
          >
            MUTUAL TRUST COOPERATIVE MULTIPURPOSE SOCIETY LIMITED
          </Typography>
          <Typography
            sx={{
              fontSize: '11pt',
              fontWeight: 800,
              color: '#D97706',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              mt: 0.5,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: '8.5pt',
                color: '#475569',
                mt: 0.25,
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            textAlign: 'right',
            minWidth: 200,
            fontSize: '8pt',
            color: '#334155',
            lineHeight: 1.45,
          }}
        >
          <div>
            <strong style={{ color: '#0B3B60' }}>Statement Date:</strong> {currentDate} ({currentTime})
          </div>
          {period && (
            <div>
              <strong style={{ color: '#0B3B60' }}>Filtered Period:</strong> {period}
            </div>
          )}
          {account && (
            <div>
              <strong style={{ color: '#0B3B60' }}>Target Account:</strong> {account}
            </div>
          )}
          {officer && (
            <div>
              <strong style={{ color: '#0B3B60' }}>Generated By:</strong> {officer}
            </div>
          )}
        </Box>
      </Box>

      {/* Optional Financial KPI Summary Row in Print Header */}
      {metrics && metrics.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${metrics.length}, 1fr)`,
            gap: 1.5,
            pt: 1,
            mt: 1,
            borderTop: '1px dashed #CBD5E1',
          }}
        >
          {metrics.map((m, idx) => (
            <Box
              key={idx}
              sx={{
                p: 0.75,
                border: '1px solid #E2E8F0',
                borderRadius: '4px',
                backgroundColor: '#F8FAFC',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '7pt',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#64748B',
                  letterSpacing: '0.02em',
                }}
              >
                {m.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '10.5pt',
                  fontWeight: 800,
                  color: m.color || '#0B3B60',
                  mt: 0.25,
                }}
              >
                {m.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

PrintReportHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  period: PropTypes.string,
  account: PropTypes.string,
  officer: PropTypes.string,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      color: PropTypes.string,
    })
  ),
};
